import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import {
  listNoteChoiceSpellings,
  noteChoicePrint,
  type NoteEditorChoiceValue,
} from '@/entities/templates';

import { NOTE_OWN_VALUES_MAX_COUNT } from '../config';

import {
  addNoteOwnOption,
  clearNoteBlock,
  combineNoteOperations,
  setNoteBlock,
  type NoteOperation,
} from './noteOperations';
import type { SurfaceBlock } from './noteSurface';
import {
  findNamedNoteValue,
  getNoteBlockValues,
  isNoteBlockMultiple,
  listHeldNoteValues,
  noteBlockEditKey,
  printNoteNone,
  type NoteBlockDef,
} from './noteView';
import { listProtocolValueSeparators } from './templateDraft';

type ChoiceBlock = Pick<SurfaceBlock, 'id' | 'selection' | 'ownOptions'>;

// What a choice block offers and holds: the template's or the list's values,
// the own options "Other" added, and the chosen values in the order the
// note prints them (the offered ones in their order, then the own ones, then
// the own text of a block that takes one).
const readChoice = (
  block: ChoiceBlock,
  def: NoteBlockDef,
  lists: NoteList[],
) => {
  const offered = getNoteBlockValues(def, lists);
  if (!offered) {
    return null;
  }
  const multiple = isNoteBlockMultiple(def);
  const { selection } = block;
  const held = multiple ? listHeldNoteValues(selection) : selection.values;
  const values = [
    ...offered,
    ...held.filter((value) => !offered.includes(value)),
  ];
  const other = multiple ? '' : selection.other;
  return {
    offered,
    multiple,
    ownOptions: block.ownOptions.filter(
      (option) =>
        !findNamedNoteValue(offered, option, (value) =>
          listNoteChoiceSpellings(def, value),
        ),
    ),
    chosen: [
      ...values.filter((value) => held.includes(value)),
      ...(other && !values.includes(other) ? [other] : []),
    ],
  };
};

// The chosen values of a choice block as the note prints them, each after
// its separator; none while the block's list is loading.
export const listNoteChoiceText = (
  block: ChoiceBlock,
  def: NoteBlockDef,
  lists: NoteList[],
  language?: string,
): NoteEditorChoiceValue[] => {
  if (block.selection.none) {
    return [
      {
        text: printNoteNone(def.Kind.value as { NoneLabel?: string }, language),
        separator: '',
      },
    ];
  }
  const chosen = readChoice(block, def, lists)?.chosen ?? [];
  const separators = listProtocolValueSeparators(chosen.length);
  return chosen.map((value, index) => ({
    text: noteChoicePrint(def, value),
    separator: separators[index],
  }));
};

// A choice set to these values: a block that takes one holds an offered
// value, or else its own text.
const setChoice = (
  block: ChoiceBlock,
  lineId: string,
  offered: string[],
  multiple: boolean,
  values: string[],
) => {
  if (!values.length) {
    return clearNoteBlock(block.id, lineId);
  }
  if (multiple) {
    // The own text of the block (the AI's "Other") goes as its text.
    const { other } = block.selection;
    return setNoteBlock(block.id, lineId, {
      values: values.filter((value) => !other || value !== other),
      text: other && values.includes(other) ? other : '',
    });
  }
  const [value] = values;
  return offered.includes(value)
    ? setNoteBlock(block.id, lineId, { values: [value] })
    : setNoteBlock(block.id, lineId, { text: value });
};

// One chosen value taken off, by its place among the printed ones: the other
// values stay, and a block left with none, or holding its explicit none, is
// cleared. Null when the block no longer holds that value.
export const removeNoteChoiceValue = ({
  block,
  def,
  lists,
  lineId,
  index,
}: {
  block: ChoiceBlock;
  def: NoteBlockDef;
  lists: NoteList[];
  lineId: string;
  index: number;
}): NoteOperation | null => {
  if (block.selection.none) {
    return clearNoteBlock(block.id, lineId);
  }
  const choice = readChoice(block, def, lists);
  if (!choice || index < 0 || index >= choice.chosen.length) {
    return null;
  }
  return setChoice(
    block,
    lineId,
    choice.offered,
    choice.multiple,
    choice.chosen.filter((_, at) => at !== index),
  );
};

// Text typed over chosen values, by their places among the printed ones.
// Each replaces only its value: emptied, the value is taken off, as its ✕
// takes it off; naming an option (ignoring case and spacing), that option
// is chosen; any other text is an own option of the block in this note,
// chosen in its place, and the template's options keep their wording. Null
// when the block no longer holds a value typed over, or can take no more
// own values: the note keeps the values it printed.
export const typeNoteChoiceValues = ({
  block,
  def,
  lists,
  lineId,
  edits,
}: {
  block: ChoiceBlock;
  def: NoteBlockDef;
  lists: NoteList[];
  lineId: string;
  edits: { index: number; text: string }[];
}): NoteOperation | null => {
  const typing = (operation: NoteOperation | null): NoteOperation | null =>
    operation && {
      ...operation,
      key: noteBlockEditKey(block.id),
      typing: true,
    };
  const typed = edits.map(({ index, text }) => ({
    index,
    text: text.trim().replace(/\s+/g, ' '),
  }));
  if (typed.every(({ text }) => !text) && block.selection.none) {
    return typing(clearNoteBlock(block.id, lineId));
  }
  const choice = readChoice(block, def, lists);
  if (!choice || typed.some(({ index }) => index >= choice.chosen.length)) {
    return null;
  }
  const { offered, multiple, ownOptions, chosen } = choice;
  const named = typed.map(({ index, text }) => ({
    index,
    text,
    known:
      text &&
      findNamedNoteValue([...offered, ...ownOptions], text, (value) =>
        listNoteChoiceSpellings(def, value),
      ),
  }));
  const values = [
    ...new Set(
      chosen.flatMap((value, at) => {
        const edit = named.find(({ index }) => index === at);
        if (!edit) {
          return [value];
        }
        return edit.text ? [edit.known || edit.text] : [];
      }),
    ),
  ];
  const added = named.flatMap(({ text, known }) =>
    text && !known ? [text] : [],
  );
  if (!added.length) {
    return typing(setChoice(block, lineId, offered, multiple, values));
  }
  const ownValues = chosen.filter((value) => !offered.includes(value));
  if (
    ownOptions.length + added.length > NOTE_OWN_VALUES_MAX_COUNT ||
    (multiple && ownValues.length + added.length > NOTE_OWN_VALUES_MAX_COUNT)
  ) {
    return null;
  }
  // Scribe makes the new option the value of a block that takes one, and
  // one more value of a block that takes several.
  return typing(
    multiple
      ? combineNoteOperations([
          ...added.map((text) => addNoteOwnOption(block.id, lineId, text)),
          setChoice(block, lineId, offered, multiple, values),
        ])
      : addNoteOwnOption(block.id, lineId, added[0]),
  );
};

// Text typed over one chosen value (`typeNoteChoiceValues`).
export const typeNoteChoiceValue = ({
  index,
  text,
  ...rest
}: {
  block: ChoiceBlock;
  def: NoteBlockDef;
  lists: NoteList[];
  lineId: string;
  index: number;
  text: string;
}) => typeNoteChoiceValues({ ...rest, edits: [{ index, text }] });
